import {
    DataTableCustomized,
    createDateFormatter,
    createNumberFormatter,
    createOptionsFormatter,
    type DataTableAction,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import { CreateEditVatRateModalProps } from '@/modals/ProductGroupVatModals/CreateEdit'
import {
    deleteVatRate,
    getAllVatRates,
} from '@/modules/Settings/services/product-group-vat'
import {
    ProductGroupVatRate,
    ProductGroupWithVatRates,
} from '@/modules/Settings/types/product-group-vat'
import dayjs from 'dayjs'
import { Plus } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const isActive = (vatRate: ProductGroupVatRate) => {
    const now = dayjs()
    const from = dayjs(vatRate.effective_from)
    const to = vatRate.effective_to ? dayjs(vatRate.effective_to) : null
    return now.isAfter(from) && (!to || now.isBefore(to))
}

const getProductGroupColumns = (
    t: (key: string) => string
): DataTableColumn<ProductGroupWithVatRates>[] => [
    {
        key: 'product_group_name',
        title: t('product-group-name'),
        width: 300,
    },
    {
        key: 'vat_rates',
        title: t('vat-rates-count'),
        width: 120,
        cellClassName: 'text-right',
        valueGetter: (row) => row.vat_rates?.length || 0,
        formatter: createNumberFormatter({ decimals: 0 }),
    },
]

const getVatRateColumns = (
    t: (key: string) => string,
    onEdit: (vatRate: ProductGroupVatRate) => void,
    onDelete: (vatRate: ProductGroupVatRate) => void
): DataTableColumn<ProductGroupVatRate>[] => [
    {
        key: 'id',
        title: 'ID',
        width: 70,
    },
    {
        key: 'tax_rate',
        title: t('tax-rate'),
        width: 120,
        cellClassName: 'text-right',
        formatter: createNumberFormatter({ decimals: 2, suffix: '%' }),
    },
    {
        key: 'effective_from',
        title: t('effective-from'),
        width: 150,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'effective_to',
        title: t('effective-to'),
        width: 150,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'description',
        title: t('description'),
        width: 300,
        valueGetter: (row) => row.description || '-',
    },
    {
        key: 'status',
        title: t('status'),
        width: 100,
        valueGetter: (row) => isActive(row),
        formatter: createOptionsFormatter({
            options: [
                { label: t('active'), value: true, color: 'success' },
                { label: t('inactive'), value: false, color: 'error' },
            ],
            renderMode: 'chip',
            textTransform: 'capitalize',
        }),
    },
    {
        key: 'id' as any,
        title: '',
        width: 80,
        render: (_, row) => (
            <div className="flex items-center gap-1">
                <button
                    className="p-1 text-blue-600 hover:text-blue-800"
                    onClick={() => onEdit(row)}
                    title={t('edit')}
                >
                    <EditIcons className="w-4 h-4" />
                </button>
                <button
                    className="p-1 text-red-600 hover:text-red-800"
                    onClick={() => onDelete(row)}
                    title={t('delete')}
                >
                    <DeleteOutlineIcons className="w-4 h-4" />
                </button>
            </div>
        ),
    },
]

const VatRatesTab: React.FC = () => {
    const { t } = useTranslation('settings')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()

    const {
        data: productGroups = [],
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.PRODUCT_GROUP_VAT_RATES],
            queryFn: getAllVatRates,
        },
        { showLoading: true, showError: true }
    )

    const { mutateAsync: removeVatRate } = useMutationWithHandler(
        {
            mutationFn: ({
                code,
                vatRateId,
            }: {
                code: string
                vatRateId: number
            }) => deleteVatRate(code, vatRateId),
        },
        {
            successMessage: t('vat-rate-deleted-successfully'),
            reactQueryKeys: [QUERY_KEYS.PRODUCT_GROUP_VAT_RATES],
        }
    )

    const handleCreate = (productGroupCode: string) => {
        setModal<CreateEditVatRateModalProps>(
            'ProductGroupVatModals/CreateEdit/index',
            { productGroupCode, vatRate: null }
        )
    }

    const handleEdit = (vatRate: ProductGroupVatRate) => {
        setModal<CreateEditVatRateModalProps>(
            'ProductGroupVatModals/CreateEdit/index',
            {
                productGroupCode: vatRate.product_group_code,
                vatRate,
            }
        )
    }

    const handleDelete = (vatRate: ProductGroupVatRate) => {
        setConfirmation({
            title: t('delete-vat-rate'),
            description: t('confirm-delete-vat-rate', {
                description: vatRate.description || `#${vatRate.id}`,
            }),
            onConfirm: () =>
                removeVatRate({
                    code: vatRate.product_group_code,
                    vatRateId: vatRate.id,
                }),
        })
    }

    const productGroupColumns = useMemo(() => getProductGroupColumns(t), [t])
    const vatRateColumns = useMemo(
        () => getVatRateColumns(t, handleEdit, handleDelete),
        [t]
    )

    const productGroupActions: DataTableAction<ProductGroupWithVatRates>[] =
        useMemo(
            () => [
                {
                    key: 'add-vat',
                    label: t('add-vat-rate'),
                    icon: <Plus className="w-4 h-4" />,
                    variant: 'info',
                    onClick: (row) => handleCreate(row.product_group_code),
                },
            ],
            [t]
        )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <DataTableCustomized<ProductGroupWithVatRates>
            columns={productGroupColumns}
            rows={productGroups}
            rowKey={(row) => row.product_group_code}
            isFetching={isFetching}
            emptyMessage={t('no-data')}
            bordered
            actions={productGroupActions}
            expandable={{
                expandedColumns: vatRateColumns,
                expandedRowData: (row) => row.vat_rates || [],
                expandedRowKey: (rate) => rate.id,
                rowExpandable: () => true,
            }}
        />
    )
}

export default VatRatesTab
